import { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlassIcon, XIcon, WarningCircleIcon, ArrowClockwiseIcon } from "@phosphor-icons/react";

// Comparación sin mayúsculas, tildes ni espacios / guiones de más (nombre, razón social, RTN o código)
export const normProv = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
const compacto = (t) => normProv(t).replace(/[\s.\-_/]/g, "");

export function coincideProveedor(s, q) {
  const n = normProv(q);
  if (!n) return true;
  const c = compacto(q);
  const campos = [s.nombre, s.razon_social, s.nombre_comercial, s.rtn, s.codigo];
  return campos.some((v) => normProv(v).includes(n)) || (c.length > 0 && campos.some((v) => compacto(v).includes(c)));
}

/**
 * Buscador del catálogo de proveedores (Compras: orden de compra, factura de proveedor, solicitud).
 * estado: "cargando" | "error" | "ok" — distingue un error al cargar el catálogo de una búsqueda sin coincidencias.
 * `seleccionado` muestra el proveedor del documento aunque ya no esté en el catálogo activo (p. ej. inactivo).
 */
export default function SupplierSearch({ suppliers, estado = "ok", onRetry, value, onPick, disabled, seleccionado, mensajeError, testid = "supplier-search" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(0);
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(null);
  const elegido = suppliers.find((s) => s.id === value) || (value && seleccionado ? { id: value, ...seleccionado } : null);

  const filtrados = useMemo(() => suppliers.filter((s) => coincideProveedor(s, q)).slice(0, 50), [q, suppliers]);

  useEffect(() => {
    if (!open || !wrapRef.current) { setPos(null); return; }
    // Abre hacia arriba si no cabe debajo (p. ej. en secciones al final del formulario) y sigue al campo al desplazarse
    const ubicar = () => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      const abajo = window.innerHeight - r.bottom - 8, arriba = r.top - 8;
      const haciaArriba = abajo < 200 && arriba > abajo;
      const maxHeight = Math.max(120, Math.min(320, haciaArriba ? arriba : abajo));
      setPos({ position: "fixed", left: r.left, width: Math.max(r.width, 380), maxHeight, zIndex: 60,
        ...(haciaArriba ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    };
    ubicar();
    window.addEventListener("scroll", ubicar, true);
    window.addEventListener("resize", ubicar);
    return () => { window.removeEventListener("scroll", ubicar, true); window.removeEventListener("resize", ubicar); };
  }, [open, q, filtrados.length]);

  const pick = (s) => { onPick(s.id, s); setQ(""); setOpen(false); };

  if (estado === "error") {
    return (
      <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/40 rounded-md px-3 py-2 text-xs text-red-300" data-testid={`${testid}-error`}>
        <WarningCircleIcon size={15} /> {mensajeError || "No se pudo cargar el catálogo de proveedores."}
        {onRetry && <button type="button" onClick={onRetry} className="ml-auto flex items-center gap-1 underline hover:text-red-200"><ArrowClockwiseIcon size={13} /> Reintentar</button>}
      </div>
    );
  }
  if (elegido && (!open || disabled)) {
    return (
      <div className="flex items-center gap-2 bg-agro-green/10 border border-agro-green/40 rounded-md px-3 py-2" data-testid={testid}>
        {elegido.codigo && <span className="font-mono text-[11px] text-agro-green">{elegido.codigo}</span>}
        <span className="text-sm text-text-primary flex-1 truncate" data-testid={`${testid}-nombre`}>{elegido.nombre}</span>
        {elegido.rtn && <span className="text-[11px] text-text-muted">RTN {elegido.rtn}</span>}
        {!disabled && (
          <button type="button" onClick={() => { setOpen(true); setQ(""); }} data-testid={`${testid}-change`}
            className="text-[11px] text-text-muted hover:text-agro-yellow underline">cambiar</button>
        )}
      </div>
    );
  }
  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      <div className="flex items-center bg-app border border-border rounded-md focus-within:border-agro-green">
        <MagnifyingGlassIcon size={14} className="text-text-muted ml-3" />
        <input type="text" value={q} disabled={disabled || estado === "cargando"} autoComplete="off"
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHover(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => Math.min(h + 1, filtrados.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setHover((h) => Math.max(h - 1, 0)); }
            else if (e.key === "Enter" && filtrados[hover]) { e.preventDefault(); pick(filtrados[hover]); }
            else if (e.key === "Escape") setOpen(false);
          }}
          placeholder={estado === "cargando" ? "Cargando proveedores…" : `Buscar proveedor por nombre, razón social, RTN o código (${suppliers.length})`}
          data-testid={`${testid}-input`}
          className="w-full bg-transparent px-2 py-2 text-sm focus:outline-none disabled:opacity-60" />
        {q && <button aria-label="Limpiar" type="button" onClick={() => setQ("")} className="text-text-muted hover:text-text-primary px-2"><XIcon size={13} /></button>}
      </div>
      {open && estado === "ok" && pos && (
        <div style={pos} className="bg-card border border-border rounded-md shadow-xl overflow-y-auto" data-testid={`${testid}-list`}
          onMouseDown={(e) => e.preventDefault()}>
          {suppliers.length === 0 ? (
            <div className="px-3 py-2 text-xs text-text-muted" data-testid={`${testid}-vacio`}>No hay proveedores activos en el catálogo.</div>
          ) : filtrados.length === 0 ? (
            <div className="px-3 py-2 text-xs text-text-muted" data-testid={`${testid}-sin-coincidencias`}>
              Ningún proveedor coincide con «{q.trim()}». Revisa el nombre, el RTN o el código.
            </div>
          ) : filtrados.map((s, k) => (
            <button type="button" key={s.id} onClick={() => pick(s)} onMouseEnter={() => setHover(k)}
              data-testid={`${testid}-opt-${s.codigo}`}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 border-b border-border/40 last:border-0 ${k === hover ? "bg-white/[0.05]" : "hover:bg-white/[0.02]"}`}>
              <span className="font-mono text-[11px] text-agro-green shrink-0">{s.codigo}</span>
              <span className="text-sm text-text-primary flex-1 truncate">{s.nombre}{s.razon_social && s.razon_social !== s.nombre ? <span className="text-text-muted"> · {s.razon_social}</span> : null}</span>
              {s.rtn && <span className="text-[11px] text-text-muted shrink-0">RTN {s.rtn}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
